import { NAVIGATION_COLORS, PATH_LABELS } from './constants';

// Get navigation color for a path
export const getNavigationColor = (path) => {
  return NAVIGATION_COLORS[path] || '#1976d2';
};

// Get user-friendly label for a path
export const getPathLabel = (path) => {
  // First check for exact match
  if (PATH_LABELS[path]) {
    return PATH_LABELS[path];
  }

  // Then check for dynamic paths
  const dynamicPaths = Object.keys(PATH_LABELS).filter(key => key.endsWith('/'));
  const matchingDynamicPath = dynamicPaths.find(key => path.startsWith(key));
  if (matchingDynamicPath) {
    return PATH_LABELS[matchingDynamicPath];
  }

  // Finally, try to generate a readable label from the path
  const lastSegment = path.split('/').pop();
  if (lastSegment) {
    // Convert kebab-case to Title Case
    return lastSegment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return 'Unknown';
};

// Get color for a path based on navigation structure
export const getColorForPath = (path, navLinks) => {
  // Find the navigation item that matches this path
  const findNavItem = (items, targetPath) => {
    for (const item of items) {
      if (item.path === targetPath) {
        return item;
      }
      if (item.children) {
        const found = findNavItem(item.children, targetPath);
        if (found) {
          return { ...found, parentText: item.text };
        }
      }
    }
    return null;
  };

  const navItem = findNavItem(navLinks, path);
  if (navItem) {
    if (navItem.parentText) {
      // If it's a child item, use parent's color
      return getNavigationColor(navItem.parentText);
    } else {
      // If it's a parent item, use its own color
      return getNavigationColor(navItem.text);
    }
  }
  
  // Fallback color mapping for common paths
  if (path.includes('/admin/')) return NAVIGATION_COLORS['Admin'] || '#673AB7';
  if (path.includes('/inpatient/')) return NAVIGATION_COLORS['Inpatient'] || '#3F51B5';
  if (path.includes('/procedures/')) return NAVIGATION_COLORS['Procedures'] || '#00BCD4';
  if (path.includes('/revenue/')) return NAVIGATION_COLORS['Revenue Management'] || '#9C27B0';
  
  return '#1976d2'; // Default blue
};

// Filter navigation links based on search query
export const filterNavLinks = (links, query) => {
  if (!query) return links;
  
  return links.filter(item => {
    // Check if parent item matches
    const parentMatches = item.text.toLowerCase().includes(query.toLowerCase());
    
    // Check if any child matches
    const childMatches = item.children?.some(child => 
      child.text.toLowerCase().includes(query.toLowerCase())
    );
    
    // Return item if parent or any child matches
    return parentMatches || childMatches;
  });
};

// Filter child items based on user roles
export const filterChildrenByRole = (children, userRoles = []) => {
  if (!children) return children;
  return children.filter(child => {
    if (!child.roles) return true;
    // Check if any of the user's roles match the child's allowed roles
    return userRoles.some(userRole => child.roles.includes(userRole));
  });
};

// Highlight matching text in navigation
export const highlightText = (text, searchTerm) => {
  if (!searchTerm) return text;
  
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) => {
    if (regex.test(part)) {
      return (
        <span key={index} style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.3)', 
          color: 'white',
          fontWeight: 'bold',
          padding: '1px 4px',
          borderRadius: '4px',
          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
        }}>
          {part}
        </span>
      );
    }
    return part;
  });
};

// Check if a path is selected (including nested routes)
export const isPathSelected = (path, currentPath) => {
  return currentPath === path || currentPath.startsWith(path + '/');
}; 